/**
 * Iter 59 — Alpha feedback capture.
 *
 * Tiny floating button bottom-left in Shell, opens a 4-category dialog.
 * Category: P0 Broken · P1 Confusing · P2 Slow · P3 Wishlist.
 * Captures current page + user agent automatically.
 *
 * Intent: every field user can fire feedback in 5 seconds without leaving
 * the screen they're on.
 */
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { MessageSquarePlus, AlertOctagon, HelpCircle, Snail, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "P0", icon: AlertOctagon, label: "Broken",     hint: "Something doesn't work or blocks my job" },
  { id: "P1", icon: HelpCircle,    label: "Confusing",  hint: "I couldn't figure out how to do something" },
  { id: "P2", icon: Snail,         label: "Slow",        hint: "It works but takes too long" },
  { id: "P3", icon: Sparkles,      label: "Wishlist",    hint: "I wish ForgedOps could do this" },
];

export default function FeedbackTrigger() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Send feedback to ForgedOps"
        data-testid="feedback-trigger"
        className="h-11 flex items-center gap-2 px-3 rounded-md text-sm font-medium text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] hover:bg-[var(--fop-surface-2)] transition-colors border border-transparent hover:border-[var(--fop-border)]"
      >
        <MessageSquarePlus size={16} />
        <span>Send feedback</span>
      </button>
      <FeedbackDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function FeedbackDialog({ open, onClose }) {
  const location = useLocation();
  const [category, setCategory] = useState("P1");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const path = location.pathname + (location.search || "");
  const sheetMatch = path.match(/\/sheet\/([^/?]+)/);
  const projectMatch = path.match(/\/project\/([^/?]+)/);

  async function submit(e) {
    e.preventDefault();
    if (message.trim().length < 4) {
      toast.error("Please share at least a few words.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        category,
        message: message.trim(),
        page_path: path,
        sheet_id: sheetMatch ? sheetMatch[1] : undefined,
        project_id: projectMatch ? projectMatch[1] : undefined,
        user_agent: navigator.userAgent,
      });
      toast.success("Thanks — feedback sent to the ForgedOps team.");
      setMessage("");
      setCategory("P1");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send feedback. Please try again.");
    } finally { setSubmitting(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle>Send feedback to ForgedOps</DialogTitle>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">
            Tell us what you ran into. We read every one during alpha.
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-3">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-2">
              What kind?
            </div>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = category === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    data-testid={`feedback-cat-${c.id}`}
                    className={`text-left rounded-md border min-h-[64px] px-3 py-2 transition-colors ${
                      active
                        ? "border-[var(--fop-primary)] bg-[var(--fop-primary-soft)] text-[var(--fop-text)]"
                        : "border-[var(--fop-border)] hover:border-[var(--fop-border-2)] text-[var(--fop-text-2)]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={14} className={active ? "text-[var(--fop-primary)]" : "text-[var(--fop-text-3)]"} />
                      <div className="text-sm font-semibold">
                        <span className="font-mono mr-1.5 text-xs">{c.id}</span>
                        {c.label}
                      </div>
                    </div>
                    <div className="text-xs text-[var(--fop-text-3)] mt-1">{c.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-2">
              What happened?
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Tapped the structure but the document panel didn't open."
              rows={4}
              className="w-full rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[var(--fop-text)] text-sm p-3 focus:border-[var(--fop-primary)] outline-none resize-none"
              data-testid="feedback-message"
              autoFocus
            />
          </div>

          <div className="text-xs text-[var(--fop-text-3)] font-mono">
            Sending: <span className="text-[var(--fop-text-2)]">{path}</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}
                     className="bg-[var(--fop-cta-bg)] hover:bg-[var(--fop-cta-bg-hover)] text-white"
                     data-testid="feedback-submit">
              {submitting ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
